import { Box } from '@atlaskit/primitives/compiled/box';

import { createPlacementFrontend } from '@post-office/message-maker-browser/create-placement-frontend';

import {
	type DefaultMessagesResponse,
	type FrontendStage,
	type NoProps,
	type PlacementMessageFrontendFactory,
} from '@post-office/message-maker-browser/types';

import { usePlacementApi } from '@post-office/use-placement-api';

import { useCallback, useEffect, useState } from 'react';

import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';

export type PlacementRenderProps = {
	onDismiss?: VoidFunction;
};

type PlacementMessageFactories = { render: FrontendStage<PlacementRenderProps> };

export type PlacementFrontendProps = NoProps;

const placementConfig = {
	placementId: 'adminhub-billing-preview-footer-section',
	options: {
		choreographer: {
			// Inline banner, not a pop-up: the choreographer TTL would unmount it after 5 minutes.
			isDefaultEnabled: false,
		},
		hideInIsolatedCloud: true,
	},
};

const createFrontend = createPlacementFrontend<PlacementMessageFactories>(placementConfig);

const CONTACT_SALES_MESSAGE_TEMPLATE_ID = 'adminhub-billing-preview-pls-contact-sales';
const TWC_DISCOVERY_MESSAGE_TEMPLATE_ID = 'adminhub-billing-preview-twc-discovery';
// TODO(cleanup): remove the TwC discovery branch once its cohort experiments end.
const TWC_DISCOVERY_EXPERIMENT_BY_COHORT: Record<string, string> = {
	'jc-paid': 'adminhub-billing-preview-twc-discovery-jc-paid-ab',
	other: 'adminhub-billing-preview-twc-discovery-ab',
};

export const PlacementFrontend: PlacementMessageFrontendFactory<
	PlacementMessageFactories,
	PlacementFrontendProps
> = createFrontend<PlacementFrontendProps>((Messages) => () => {
	const [message, setMessage] = useState<DefaultMessagesResponse['messages'][number]>();
	const { data, error, isLoading } = usePlacementApi<DefaultMessagesResponse>('/');

	useEffect(() => {
		if (!data?.messages.length || error || isLoading) {
			return;
		}

		const prioritizedMessages = [
			...data.messages.filter((msg) => msg.messageTemplateId === TWC_DISCOVERY_MESSAGE_TEMPLATE_ID),
			...data.messages.filter((msg) => msg.messageTemplateId !== TWC_DISCOVERY_MESSAGE_TEMPLATE_ID),
		];

		for (const msg of prioritizedMessages) {
			if (msg.messageTemplateId === CONTACT_SALES_MESSAGE_TEMPLATE_ID) {
				try {
					if (
						// oxlint-disable-next-line post-office/no-manual-in-app-experiments -- Legacy experiment handling is retained without migration.
						FeatureGates.getExperimentValue<string>(
							'twcg-1533-pls-contact-path-disco',
							'cohort',
							'not-enrolled',
							{ fireExperimentExposure: true },
						) !== 'variation'
					) {
						continue;
					}
				} catch {
					continue;
				}
			}

			if (msg.messageTemplateId === TWC_DISCOVERY_MESSAGE_TEMPLATE_ID) {
				const { experimentCohort } = msg as { experimentCohort?: unknown };
				const experimentName =
					typeof experimentCohort === 'string'
						? TWC_DISCOVERY_EXPERIMENT_BY_COHORT[experimentCohort]
						: undefined;
				try {
					if (
						!experimentName ||
						// oxlint-disable-next-line post-office/no-manual-in-app-experiments -- Placement config cannot pick an experiment per cohort.
						FeatureGates.getExperimentValue<string>(experimentName, 'cohort', 'not-enrolled', {
							fireExperimentExposure: true,
						}) !== 'variation'
					) {
						continue;
					}
				} catch {
					continue;
				}
			}

			setMessage(msg);
			break;
		}
	}, [data?.messages, error, isLoading]);

	const handleDismiss = useCallback((): void => setMessage(undefined), []);

	if (error || isLoading || !message) {
		return null;
	}

	return (
		<Box>
			<Messages.render {...message} onDismiss={handleDismiss} />
		</Box>
	);
});
